import { Link } from "react-router-dom";
import { ArrowRightIcon, BookOpenIcon, QuestionMarkCircleIcon } from "@heroicons/react/24/outline";
import type { Guide } from "../../../lib/help/types";
import { getRelatedFaqs, getRelatedGuides } from "../../../lib/help/related";

export function RelatedContent({ guide }: { guide: Guide }) {
  const relatedGuides = getRelatedGuides(guide.frontmatter.slug, 3);
  const relatedFaqs = getRelatedFaqs(guide.frontmatter.tags, undefined, 3);

  if (!relatedGuides.length && !relatedFaqs.length) return null;

  return (
    <section className="mt-16 rounded-2xl border border-cream-300 bg-cream-100/60 p-6 md:p-8">
      <h2 className="font-display text-2xl font-semibold text-bark-900">Related reading</h2>
      <p className="mt-1 text-sm text-bark-700">
        More guides and answers that follow naturally from this one.
      </p>

      <div className="mt-6 grid gap-8 md:grid-cols-2">
        {relatedGuides.length > 0 && (
          <div>
            <p className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-forest-700">
              <BookOpenIcon className="h-4 w-4" aria-hidden="true" />
              Guides
            </p>
            <ul className="space-y-3">
              {relatedGuides.map(({ frontmatter }) => (
                <li key={frontmatter.slug}>
                  <Link
                    to={`/help/guides/${frontmatter.slug}`}
                    className="group block rounded-lg border border-transparent p-3 transition-colors hover:border-forest-300 hover:bg-cream-50"
                  >
                    <span className="flex items-start justify-between gap-3">
                      <span className="font-display text-base font-semibold text-bark-900 group-hover:text-forest-700">
                        {frontmatter.title}
                      </span>
                      <ArrowRightIcon className="mt-1 h-4 w-4 flex-shrink-0 text-forest-600 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
                    </span>
                    <span className="mt-1 block text-sm text-bark-700">{frontmatter.description}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        )}

        {relatedFaqs.length > 0 && (
          <div>
            <p className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-forest-700">
              <QuestionMarkCircleIcon className="h-4 w-4" aria-hidden="true" />
              FAQs
            </p>
            <ul className="space-y-3">
              {relatedFaqs.map((entry) => (
                <li key={entry.id}>
                  <Link
                    to={`/help/faq#${entry.id}`}
                    className="group block rounded-lg border border-transparent p-3 transition-colors hover:border-forest-300 hover:bg-cream-50"
                  >
                    <span className="flex items-start justify-between gap-3">
                      <span className="font-display text-base font-semibold text-bark-900 group-hover:text-forest-700">
                        {entry.question}
                      </span>
                      <ArrowRightIcon className="mt-1 h-4 w-4 flex-shrink-0 text-forest-600 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
                    </span>
                    <span className="mt-1 block text-sm text-bark-700">Read the answer in the FAQ.</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </section>
  );
}
